import { Delete, Edit } from '@mui/icons-material'
import {
	Button,
	IconButton,
	TableCell
} from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { UserProperty, UserPropertySimple } from '../../../../api/DTOtemp'
import { UserPropertiesClient } from '../../../../api/clients/identity'
import { DeleteDialog } from '../../../../components/dialogs/DeleteDialog'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import IconButtonLink from '../../../../components/links/IconButtonLink'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import { FilterControl } from '../../../../components/tables/filters/DTO'
import { toastService } from '../../../../services/notifications/ToastService'
import { makeChildRoute } from '../../../../utils/CreateChildRoute'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { CreateUserPropertyDialog } from './CreateUserPropertyDialog'

export const UserPropertiesPage = observer(() => {
	const { url } = useRouteMatch()
	const userPropertiesClient = new UserPropertiesClient()
	const history = useHistory()

	const store = useLocalObservable(() => ({
		properties: undefined as UserProperty[] | undefined,
		createPropertyOpen: false,
		propertyToDelete: undefined as UserProperty | undefined,
	}))

	useEffect(() => {
		userPropertiesClient.GetUserProperties(true).then(
			action((v) => {
				store.properties = v.data
			}),
		)
	}, [])

	const handleShowCreateProperty = action(() => {
		store.createPropertyOpen = true
	})

	const handleCloseCreateProperty = action(() => {
		store.createPropertyOpen = false
	})

	const handleCreateProperty = async (property: UserPropertySimple) => {
		store.createPropertyOpen = false

		userPropertiesClient
			.CreateUserProperty(property)
			.then((response) => {
				const propertyId = response.data.id

				toastService.displayToast({
					message: 'User property created',
					area: 'global',
					severity: 'success',
				})

				history.push(`/_administration/_user-properties/${propertyId}`)
			})
			.catch(() => {
				toastService.displayToast({
					message: 'Error creating user property',
					area: 'global',
					severity: 'error',
				})
			})
	}

	const handleDeleteClicked = action((property: UserProperty) => {
		store.propertyToDelete = property
	})

	const handleDeleteDialogClosed = action(() => {
		store.propertyToDelete = undefined
	})

	const handleDeleteProperty = (propertyId: number) => {
		userPropertiesClient
			.DeleteUserProperty(propertyId)
			.then(() => {
				toastService.displayToast({
					message: 'User property deleted',
					area: 'global',
					severity: 'success',
				})
				runInAction(() => {
					if (store.properties !== undefined) {
						const propertyToDelete = store.properties
							.map((v) => v.id)
							.indexOf(propertyId)
						if (propertyToDelete !== -1)
							store.properties = [...store.properties.splice(propertyToDelete, 1)]
					}
				})
			})
			.catch(() => {
				toastService.displayToast({
					message: 'Error deleting user property',
					area: 'global',
					severity: 'error',
				})
			})
	}

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'search',
			name: 'search',
			column: "name"
		}
	], [])

	const columns: TableColumn<UserProperty>[] = useMemo(() => [
		{
			name: "name",
			label: "Name"
		},
		{
			name: "userEditable",
			label: "User Editable",
			renderContent: (p) => String(p.userEditable)
		},
		{
			renderHeading: <TableCell key={2} align="right">Actions</TableCell>,
			renderCell: (property) =>
				<TableCell key={2} align="right">
					<IconButtonLink
						to={makeChildRoute(property.id.toString(), url)}
					>
						<Edit fontSize="small" />
					</IconButtonLink>
					<IconButton
						onClick={() => handleDeleteClicked(property)}
						size="large"
					>
						<Delete fontSize="small" />
					</IconButton>
				</TableCell>
		}
	], [])

	return (
		<>
			<AdministrationPageContainer
				title="User Properties"
				actions={
					<div>
						<Button
							variant="contained"
							color="primary"
							startIcon={<Edit />}
							onClick={handleShowCreateProperty}
						>
							Create Property
						</Button>
					</div>
				}
			>
				{store.properties === undefined ? (
					<FullscreenSpinner />
				) : (
					<FilteredTable
						allEntries={store.properties}
						columns={columns}
						filterControls={filterControls}
					/>
				)}
			</AdministrationPageContainer>
			<CreateUserPropertyDialog
				open={store.createPropertyOpen}
				userPropertyNames={store.properties?.map((v) => v.name) ?? []}
				createUserProperty={handleCreateProperty}
				onClose={handleCloseCreateProperty}
			/>
			{store.propertyToDelete !== undefined && (
				<DeleteDialog<number>
					open={store.propertyToDelete !== undefined}
					itemName={store.propertyToDelete?.name}
					itemId={store.propertyToDelete?.id}
					onClose={handleDeleteDialogClosed}
					onDelete={handleDeleteProperty}
				/>
			)}
		</>
	)
})